import { useCallback, useEffect, useState } from 'react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import type { ProcedurePhase } from '../lib/types';

/**
 * Sprint 2 — panneau des actes procéduraux (61ter, 61quinquies, 136 CIC,
 * audition complémentaire, levée saisie, requête devoirs, …).
 *
 * Distincts des `ProcedureEvent` qui modélisent les transitions d'état.
 * Un acte 61ter ne change pas la phase mais déclenche un compteur de
 * récurrence à 90 jours.
 */

type Auteur = 'avocat' | 'parquet' | 'ji' | 'partie_civile' | 'tribunal' | 'autre';

type ActeType =
  | 'art_61ter'
  | 'art_61quinquies'
  | 'art_136'
  | 'audition_complementaire'
  | 'levee_saisie'
  | 'requete_devoirs'
  | 'constitution_pc'
  | 'opposition'
  | 'appel'
  | 'pourvoi'
  | 'autre';

type ActeRow = {
  id: string;
  dossier_id: string;
  phase: ProcedurePhase;
  acte_type: ActeType;
  acte_date: string;
  auteur: Auteur;
  source_pdf_id: string | null;
  pieces_liees: string[];
  commentaire: string | null;
  recurrence_jours: number | null;
  next_eligible_date: string | null;
  created_at: string;
};

const TYPE_LABELS: Record<ActeType, string> = {
  art_61ter: 'Requête 61ter (accès dossier)',
  art_61quinquies: 'Requête 61quinquies (devoir complémentaire)',
  art_136: 'Article 136 CIC (régularité instruction)',
  audition_complementaire: 'Audition complémentaire',
  levee_saisie: 'Levée de saisie',
  requete_devoirs: 'Requête de devoirs',
  constitution_pc: 'Constitution partie civile',
  opposition: 'Opposition',
  appel: "Acte d'appel",
  pourvoi: 'Pourvoi cassation',
  autre: 'Autre',
};

const AUTEUR_LABELS: Record<Auteur, string> = {
  avocat: 'Avocat',
  parquet: 'Parquet',
  ji: "Juge d'instruction",
  partie_civile: 'Partie civile',
  tribunal: 'Tribunal',
  autre: 'Autre',
};

type Props = {
  dossierId: string;
  currentPhase: ProcedurePhase;
};

export default function ActesProceduralPanel({ dossierId, currentPhase }: Props) {
  const [items, setItems] = useState<ActeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.get<ActeRow[]>(`/dossiers/${dossierId}/procedural-actes`);
      setItems(data);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDelete = async (row: ActeRow) => {
    if (!confirm(`Supprimer cet acte (${TYPE_LABELS[row.acte_type]}) ?`)) return;
    try {
      await api.delete(`/procedural-actes/${row.id}`);
      setItems((prev) => prev.filter((x) => x.id !== row.id));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Suppression échouée');
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-medium text-text-primary">Actes procéduraux</h3>
        <Btn type="button" variant="ghost" onClick={() => setAddOpen(true)}>
          <IconPlus size={14} stroke={1.6} />
          Ajouter
        </Btn>
      </div>

      {loading ? (
        <p className="text-[12px] text-text-tertiary">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule bg-paper-card p-3 text-center text-[12px] italic text-text-tertiary">
          Aucun acte procédural enregistré (61ter, 61quinquies, 136 CIC…). Clique sur « Ajouter »
          pour en créer un.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((row) => (
            <li
              key={row.id}
              className="group flex items-start gap-3 rounded-md border border-rule bg-paper-elevated p-2.5 text-[12.5px]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-text-primary">
                    {TYPE_LABELS[row.acte_type]}
                  </span>
                  <span className="font-mono text-[11px] text-text-tertiary">
                    {new Date(row.acte_date).toLocaleDateString('fr-BE')}
                  </span>
                  <span className="rounded-pill bg-paper-subtle px-1.5 py-[1px] text-[10px] text-text-secondary">
                    {AUTEUR_LABELS[row.auteur]}
                  </span>
                </div>
                {row.commentaire && (
                  <p className="mt-0.5 text-[11.5px] text-text-secondary">{row.commentaire}</p>
                )}
                {row.next_eligible_date && (
                  <p className="mt-0.5 text-[10.5px] text-cat-imp-text">
                    🔁 Prochaine éligibilité :{' '}
                    {new Date(row.next_eligible_date).toLocaleDateString('fr-BE')}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => void onDelete(row)}
                title="Supprimer"
                className="rounded p-1 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
              >
                <IconTrash size={14} stroke={1.6} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {addOpen && (
        <AddActeForm
          dossierId={dossierId}
          phase={currentPhase}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            void load();
          }}
        />
      )}
    </section>
  );
}

function AddActeForm({
  dossierId,
  phase,
  onClose,
  onSaved,
}: {
  dossierId: string;
  phase: ProcedurePhase;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [acteType, setActeType] = useState<ActeType>('art_61ter');
  const [acteDate, setActeDate] = useState(new Date().toISOString().slice(0, 10));
  const [auteur, setAuteur] = useState<Auteur>('avocat');
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/dossiers/${dossierId}/procedural-actes`, {
        phase,
        acte_type: acteType,
        acte_date: acteDate,
        auteur,
        commentaire: commentaire.trim() || null,
      });
      toast.success('Acte ajouté');
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Ajout échoué');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md space-y-3 rounded-md bg-paper-elevated p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[15px] font-medium text-text-primary">
          Nouvel acte procédural
        </h3>
        <div className="space-y-2">
          <div>
            <label className="text-[11.5px] text-text-secondary">Type</label>
            <select
              value={acteType}
              onChange={(e) => setActeType(e.target.value as ActeType)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              {(Object.keys(TYPE_LABELS) as ActeType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11.5px] text-text-secondary">Date</label>
              <input
                type="date"
                value={acteDate}
                onChange={(e) => setActeDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[11.5px] text-text-secondary">Auteur</label>
              <select
                value={auteur}
                onChange={(e) => setAuteur(e.target.value as Auteur)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                {(Object.keys(AUTEUR_LABELS) as Auteur[]).map((a) => (
                  <option key={a} value={a}>
                    {AUTEUR_LABELS[a]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11.5px] text-text-secondary">Commentaire (optionnel)</label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              placeholder="Note libre, références aux pièces…"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Btn>
          <Btn
            type="button"
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
